<script setup lang="ts">
import type { SearchSuggestSelectModalProps } from '.';
import type {
	Suggestion,
	SuggestionCondoResult
} from '~/components/SearchSuggest';
import type { SelectAddressEvent } from '~/composables/useNewSearchSuggest';
import type { MacroregionName } from '~/config/cities';

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogTitle
} from '@/components/ui/dialog';
import { useSearchSuggestNavigation } from '~/composables/useSearchSuggestNavigation';
import { useSingleFieldSuggest } from '~/composables/useSingleFieldSuggest';
import { CityMacroregionHelper } from '~/utils/AddressUtilsWrapper';

import { SEARCH_PLACEHOLDERS } from '~/components/SearchSuggest';
import SearchSuggest from '~/components/SearchSuggest/Suggest.vue';
import SuggestDialog from '~/components/SearchSuggest/SuggestDialog.vue';

const props = withDefaults(defineProps<SearchSuggestSelectModalProps>(), {
	property: null
});

const { selectedMacroregion, setMacroregion, clearMacroregion } =
	useSingleFieldSuggest();

const macroregionName = computed(() => {
	if (!props.property?.city) return undefined;

	// Using getCityInfo to get the correct macroregion based on city + region
	// This is important for cities with multiple macroregions (e.g. Santana de Parnaíba can have regions in Alphaville or Interior)
	const cityInfo = CityMacroregionHelper.getCityInfo(
		props.property.city,
		props.property.region
	);

	return cityInfo?.macroregion;
});

const emit = defineEmits<{
	(e: 'select-address', event: SelectAddressEvent): void;
	(e: 'select-place', value: SelectAddressEvent): void;
	(e: 'select-condo', value: SuggestionCondoResult): void;
	(e: 'select-code', value: Suggestion['searchParams']): void;
}>();

const { isFetchingPropertyByCodeSearch } = useSearchSuggestNavigation();

const isSearchOpen = ref(false);
const isMobileSearchOpen = ref(false);
const searchSuggestRef =
	useTemplateRef<InstanceType<typeof SearchSuggest>>('searchSuggest');

const DESKTOP_BREAKPOINT = 1024;

function isDesktopSize(): boolean {
	if (import.meta.server) return false;
	return window.innerWidth >= DESKTOP_BREAKPOINT;
}

async function handleSearchButtonClick() {
	// Setar a macroregion no state global quando abre
	if (macroregionName.value) {
		setMacroregion(macroregionName.value as MacroregionName);
	}
	if (isDesktopSize()) {
		isSearchOpen.value = true;
	} else {
		isMobileSearchOpen.value = true;
	}
}

function handleSearchClose() {
	isSearchOpen.value = false;
	isMobileSearchOpen.value = false;

	// Resetar a macroregion quando fecha
	clearMacroregion();
}

function handleInternalSelectCode(event: Suggestion['searchParams']) {
	emit('select-code', event);
}

function handleInternalSelectCondo(searchParams: SuggestionCondoResult) {
	emit('select-condo', searchParams);
}

function handleInternalSelectPlace(searchParams: SelectAddressEvent) {
	emit('select-place', searchParams);
}

function handleInternalSelectAddress(event: SelectAddressEvent) {
	emit('select-address', event);
}

function focusInput() {
	if (!searchSuggestRef.value) return;

	const DIALOG_ANIMATION_TIME = 150;

	setTimeout(() => {
		searchSuggestRef.value?.focusInput();
	}, DIALOG_ANIMATION_TIME);
}

watch(isSearchOpen, isOpen => {
	if (isOpen) {
		nextTick(focusInput);
	}
});
</script>

<template>
	<div class="relative">
		<div @click="handleSearchButtonClick">
			<slot />
		</div>

		<div class="hidden lg:block">
			<Dialog v-model:open="isSearchOpen">
				<DialogContent
					class="w-[600px] max-w-[calc(100vw-40px)] p-0"
					:show-close-icon="false"
				>
					<div class="sr-only">
						<DialogTitle></DialogTitle>
						<DialogDescription></DialogDescription>
					</div>

					<div class="overflow-hidden rounded-lg bg-white">
						<SearchSuggest
							ref="searchSuggest"
							class="w-full border-none"
							input-wrapper-classes="bg-white rounded-t-lg border-b border-white-dark-3"
							input-classes="h-[50px] md:h-14 !pl-0 text-ellipsis text-primary text-sm md:text-base placeholder:text-primary-light-1"
							input-clear-icon-wrapper-classes="flex h-full w-7 items-center justify-end pr-2"
							input-clear-icon-classes="size-5 cursor-pointer text-white-dark-3"
							input-search-icon-wrapper-classes="grid h-full w-[62px] place-items-center order-first"
							input-search-icon-classes="size-7 text-primary"
							list-wrapper-classes="border-t-0"
							:placeholders="SEARCH_PLACEHOLDERS.dialog"
							:enable-animation="false"
							:close-on-click-outside="false"
							:close-on-select="false"
							:show-close-icon="true"
							:is-fetching-property-by-code-search="
								isFetchingPropertyByCodeSearch
							"
							:is-dropdown-opened="true"
							:current-filters="{
								macroregions: macroregionName ? [macroregionName] : undefined
							}"
							@select-address="handleInternalSelectAddress"
							@select-place="handleInternalSelectPlace"
							@select-condo="handleInternalSelectCondo"
							@select-code="handleInternalSelectCode"
							@close-modal="handleSearchClose"
						/>
					</div>
				</DialogContent>
			</Dialog>
		</div>

		<div class="lg:hidden">
			<SuggestDialog
				:is-open="isMobileSearchOpen"
				:placeholders="SEARCH_PLACEHOLDERS.dialog"
				:is-fetching-property-by-code-search="isFetchingPropertyByCodeSearch"
				:show-input-button="false"
				:current-filters="{
					macroregions: macroregionName ? [macroregionName] : undefined
				}"
				@select-address="handleInternalSelectAddress"
				@select-place="handleInternalSelectPlace"
				@select-condo="handleInternalSelectCondo"
				@select-code="handleInternalSelectCode"
				@close-modal="handleSearchClose"
				@update:is-open="isMobileSearchOpen = $event"
			/>
		</div>
	</div>
</template>
